import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'loggedin_tenant_company_brand_color';
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

type Listener = () => void;

const listeners = new Set<Listener>();

function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const prefixed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (!HEX_COLOR_PATTERN.test(prefixed)) return null;
  return prefixed.length === 4
    ? `#${prefixed[1]}${prefixed[1]}${prefixed[2]}${prefixed[2]}${prefixed[3]}${prefixed[3]}`
    : prefixed.toUpperCase();
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const storage = window.localStorage;
    if (
      typeof storage?.getItem !== 'function' ||
      typeof storage?.setItem !== 'function' ||
      typeof storage?.removeItem !== 'function'
    ) {
      return null;
    }
    return storage;
  } catch {
    return null;
  }
}

function getStoredColor(): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    return normalizeHexColor(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

let tenantBrandColor: string | null = getStoredColor();

function emitChange(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string | null {
  return tenantBrandColor;
}

function getServerSnapshot(): string | null {
  return null;
}

export function setTenantBrandColor(color?: string | null): string | null {
  const normalized = normalizeHexColor(color);
  tenantBrandColor = normalized;

  const storage = getLocalStorage();
  if (storage) {
    try {
      if (normalized) {
        storage.setItem(STORAGE_KEY, normalized);
      } else {
        storage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage write failures (e.g. polluted test globals).
    }
  }

  emitChange();
  return tenantBrandColor;
}

export function getTenantBrandColor(): string | null {
  return tenantBrandColor;
}

export function useTenantBrandColor(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
